import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isFirebaseConfigured } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../common/LoadingSpinner';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute - Ensures user is authenticated and active
 *
 * Redirects to:
 * - /login if not authenticated
 * - /pending if authenticated but pending approval
 * - /suspended if account is suspended
 *
 * If Firebase is not configured, allows access (development mode)
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isPending, isSuspended, loading, profileLoading } = useAuth();
  const location = useLocation();

  // If Firebase is not configured, allow access (development mode)
  if (!isFirebaseConfigured) {
    return <>{children}</>;
  }

  // Show loading state while checking auth
  if (loading || profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Pending approval - redirect to pending page
  if (isPending) {
    return <Navigate to="/pending" replace />;
  }

  // Suspended - redirect to suspended page
  if (isSuspended) {
    return <Navigate to="/suspended" replace />;
  }

  // Active user - render children
  return <>{children}</>;
};

export default ProtectedRoute;
